// NATIVE 
import * as React from 'react';

// INSTALLED 
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import TableContainer from '@mui/material/TableContainer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { Box, Card, Pagination, TablePagination } from '@mui/material';
import { getLogsByPagination, setStoreLog, storeGetLogs } from '../../store/logs';




// CUSTOM 
import { DetailDialogLog } from '../modals/logs';
import MultipleSnackbars from '../MultipleSnackbars';
import { showNumberRowsTable } from '../../commons/utils/values';
import { closeSessionGlobal, corregirTexto, firstLetterUpperCase, usarFuncionesComposicion } from '../../commons/utils';
import { SnackAlert } from '../SnackAlert';
import { paddingCardsTables, timeToClosedToast } from '../../commons';



const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.secondary.contrastText,
    fontSize: 15,
    fontWeight: 700
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: 500,
    color: '#2e263de6'
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));




/**
 * TableLogAcciones component displays a table of logs with pagination and detailed view functionality.
 * 
 * @component
 * 
 * @example
 * return (
 *   <TableLogAcciones />
 * )
 * 
 * @returns {JSX.Element} The rendered component.
 * 
 * @description
 * This component fetches logs from the Redux store and displays them in a table format. It includes pagination, 
 * detailed view of each log, and various states to manage the UI and data.
 * 
 * @function
 * @name TableLogAcciones
 * 
 * @typedef {Object} Log
 * @property {string} name - The name of the log.
 * @property {string} description - The description of the log.
 * @property {Object} usuario - The user who created the log.
 * @property {string} usuario.name - The name of the user.
 * @property {string} ip - The IP address from which the log was created.
 * @property {Date} fecha - The date when the log was created.
 * 
 * @typedef {Object} Pagination
 * @property {number} page - The current page number.
 * @property {number} take - The number of items per page.
 * @property {number} itemCount - The total number of items.
 * @property {number} pageCount - The total number of pages.
 * @property {boolean} hasPreviousPage - Whether there is a previous page.
 * @property {boolean} hasNextPage - Whether there is a next page.
 * 
 * @state {Array<Log>} rows - The list of logs to be displayed in the table.
 * @state {boolean} modalDetailDialog - State to manage the visibility of the detail dialog.
 * @state {HTMLElement | null} anchorEl - The anchor element for the menu.
 * @state {number} page - The current page number for the table.
 * @state {number} pagePagination - The current page number for the pagination component.
 * @state {number} itemCountPagination - The total number of pages for the pagination component.
 * @state {number} rowsPerPage - The number of rows per page in the table.
 * @state {number} timeAlert - The duration for which alerts are displayed.
 * @state {Array<any>} messagesAlerts - The list of alert messages.
 * @state {boolean} modalSessionExpired - State to manage the visibility of the session expired modal.
 * @state {boolean} alert_snack - State to manage the visibility of the snack alert.
 * @state {Object} msgSnack - The message and status for the snack alert.
 * @state {Array<Log>} dataTableList - The list of logs to be displayed in the table.
 * @state {Pagination} pagination - The pagination details for the logs.
 * 
 * @hook {function} useDispatch - Hook to dispatch actions to the Redux store.
 * @hook {function} useSelector - Hook to select data from the Redux store.
 * @hook {function} useEffect - Hook to perform side effects in the component.
 * @hook {function} useState - Hook to manage state in the component.
 * 
 * @function handleClick - Handles the click event to show the detail dialog for a log.
 * @param {Log} log - The log object to be displayed in the detail dialog.
 * 
 * @function handleCloseModalDetailLog - Handles the event to close the detail dialog.
 * 
 * @function getLogAcciones - Fetches the logs from the server and updates the Redux store.
 * 
 * @function formatedDate - Formats a date to a string.
 * @param {Date} date - The date to be formatted.
 * @returns {string} The formatted date string.
 * 
 * @function handleChangePage - Handles the event to change the page in the table.
 * @param {unknown} event - The event object.
 * @param {number} newPage - The new page number.
 * 
 * @function handleChangeRowsPerPage - Handles the event to change the number of rows per page in the table.
 * @param {React.ChangeEvent<HTMLInputElement>} event - The event object.
 * 
 * @function changePaginationPage - Handles the event to change the page in the pagination component.
 * @param {React.ChangeEvent<unknown>} event - The event object.
 * @param {number} value - The new page number.
 * 
 * @function validateResponse - Validates the response from the server.
 * @param {any} response - The response object from the server.
 * @param {string} textActionConcat - The action text to be concatenated with the error message.
 * @returns {boolean} Whether the response is valid.
 */
export const TableLogAcciones = () => {

 
  // USE DISPATCH 
  const dispatch = useDispatch();


  // USE SELECTOR 
  const { log, logs_list }:any  = useSelector( (state:any) => state.logs );


  // USE STATE 
  const [rows, setRows] = React.useState<any>([]);
  const [modalDetailDialog, setModalDetailLog] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);


  const [page, setPage] = React.useState(0);
  const [pagePagination, setPagePagination] = React.useState(1);
  const [itemCountPagination, setItemCountPagination] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(showNumberRowsTable);


  const [timeAlert, setTimeAlert] = React.useState(timeToClosedToast);
  const [messagesAlerts, setMessagesAlerts] = React.useState<any>([]);
  

  const [modalSessionExpired, setModalSessionExpired] = React.useState<boolean>(false);
      
  
  const [alert_snack, setAlertSnack] = React.useState(false);
  const [msgSnack, setMsgSnack] = React.useState({
      msg: '',
      status: 'success'
  });
  
  const [dataTableList, setDataTable] = React.useState([]);
  const [pagination, setPagination] = React.useState<any>({
      "page": 1,
      "take": 1,
      "itemCount": 1,
      "pageCount": 1,
      "hasPreviousPage": false,
      "hasNextPage": false
  });



  // CONST STYLES 
  const Div = styled('div')(({ theme }) => ({
    ...theme.typography.button,
    backgroundColor: 'wheat',
    padding: theme.spacing(8),
    textAlign: 'center'
  }));



  // CONST VALUES 
  const open = Boolean(anchorEl);



  // USE EFFECT 
  React.useEffect(() => {
    
    if ( logs_list.data && logs_list.data.length > 0 ) {

      setDataTable(logs_list.data);
      setPagination({
        "page": logs_list.meta.page,
        "take": logs_list.meta.take,
        "itemCount": logs_list.meta.itemCount,
        "pageCount": logs_list.meta.pageCount,
        "hasPreviousPage": logs_list.meta.hasPreviousPage,
        "hasNextPage": logs_list.meta.hasNextPage
      });
      setItemCountPagination(logs_list.meta.pageCount);
      
    }
    

  }, [logs_list]);


  React.useEffect(() => {
    getLogAcciones();
  }, []);


  React.useEffect(() => {
    setRows(logs_list);
  }, [logs_list]);

 

  

  // CONST FUNCTIONS 
  const handleClick = async(log: object) => {
     
        await dispatch( setStoreLog(log) );
        setModalDetailLog(true);
       
  };


  const handleCloseModalDetailLog = () => {
        setModalDetailLog(false);
  }

 


  const getLogAcciones = async() => {
    
     const response =  await dispatch( storeGetLogs() );
      
     await validateResponse(response, '( Ver Log Acciones )');

  }

  
  
  
 
  const formatedDate = (date: Date) => {

    const event = new Date(date);
     return event.toLocaleString('es-CO');
  }


  const handleChangePage = (event: unknown, newPage: number) => {
    
    setPage(newPage);

  };


  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
  
        setRowsPerPage(+event.target.value);
  
        setPage(0);
  
  };



  const changePaginationPage = async(event: React.ChangeEvent<unknown>, value: number) => {
            
        setPagePagination(value)
    
        const response =  await dispatch( getLogsByPagination(value) );

        await validateResponse(response, '( Listar Log Acciones )');
        
  };



  const validateResponse = async(response:any, textActionConcat: string) => {
    
    
        if (  response.code == 401  ) {
          await localStorage.removeItem('lastSelectedQypht');
          await closeSessionGlobal();
          setModalSessionExpired(true);
          return false;
        }

      
        // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION 
        if (!response.ok && response.code == 403 ) { 

          const errors:any = {
            msg: response.errors[0]+' '+textActionConcat,
            status: 'error'
          };
        
          await setMsgSnack(errors);
          await setAlertSnack(true);

        
          return false;

        }

        
        // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO 
        if (!response.ok && response.code == 400 ) { 

            const errors:any = {
              msg: response.errors[0]+' '+textActionConcat,
              status: 'error'
            };
          
            await setMsgSnack(errors);
            await setAlertSnack(true);

          
            return false;

        }

        return true;
      
  }
  
  
  return (
    
     <div>
        
        <SnackAlert open={alert_snack} onclick={setAlertSnack} text={msgSnack.msg} severity={msgSnack.status}/>
        <MultipleSnackbars messagesList={messagesAlerts} position={"bottom-left"} autoClose={timeAlert} />
        <DetailDialogLog  open={modalDetailDialog} handleCloseModalDetailLog={handleCloseModalDetailLog} />
        

        {
          dataTableList.length > 0
                
          ?
          <>
          <Card
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 3,
                borderRadius: 2,
                boxShadow: 3,
                bgcolor: 'background.paper',
                marginTop: paddingCardsTables
              }}
            >
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 700 }} aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>#</StyledTableCell>
                      <StyledTableCell >Nombre</StyledTableCell>
                      <StyledTableCell >Descripcion</StyledTableCell>
                      <StyledTableCell >Usuario</StyledTableCell>
                      <StyledTableCell >Ip</StyledTableCell>
                      <StyledTableCell >Fecha</StyledTableCell>
                      <StyledTableCell >Acciones</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      dataTableList
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((log:any, index:any) => (
                      <StyledTableRow key={ index + 1 }>
                        <StyledTableCell component="th" scope="row">
                          {index + 1}
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="row">
                          {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, log.name)}
                        </StyledTableCell>
                        <StyledTableCell>
                          {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, log.description)}
                        </StyledTableCell>
                        <StyledTableCell component="th" scope="row">
                          {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, log.usuario.name)}
                        </StyledTableCell>
                        <StyledTableCell>
                          {log.ip}
                        </StyledTableCell>
                        <StyledTableCell>
                          { formatedDate(log.fecha) }
                        </StyledTableCell>
                        <StyledTableCell >
                              
  
                                
                        <div>
                          <Button
                            id="demo-customized-button"
                            aria-controls={open ? 'demo-customized-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            variant="text"
                            disableElevation
                            onClick={ () => handleClick(log) }
                            startIcon={ <VisibilityIcon />}
                          >
                            Detalles
                          </Button>
                        </div>
  
                      
  
  
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
          </Card>
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            labelRowsPerPage={'Filas por pagina'}
            labelDisplayedRows={({ from, to, count }) => ` ${from}-${to} de  ${count} `}
            component="div"
            count={pagination.itemCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={ handleChangePage }
            onRowsPerPageChange={ handleChangeRowsPerPage }
          />
  
          <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: 2,
                  marginBottom: 2
                }}
              >
              <Pagination sx={{ justifyContent: 'center' }} color="primary" count={itemCountPagination} page={pagePagination} onChange={ changePaginationPage} showFirstButton showLastButton />
          </Box>
          </>
          :
          <Div>{"No hay Logs disponibles..."}</Div>
        }
        
     </div>
  );
}


